import React, { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import axios from 'axios';
import Header from '../components/Header';
import SEO from '../components/SEO';
import { db } from '../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useIOTradingContract } from '../hooks/useIOTradingContract';

const FutureTrading = () => {
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [livePrices, setLivePrices] = useState({ BTC: 0, ETH: 0, SOL: 0, DOGE: 0, BNB: 0, XRP: 0 });
  const [candleData, setCandleData] = useState([]);
  const [timeframe, setTimeframe] = useState('1H');
  const [loading, setLoading] = useState(true);
  const [betAmount, setBetAmount] = useState('10');
  const [leverage, setLeverage] = useState(10);
  const [position, setPosition] = useState('LONG'); // LONG or SHORT
  const [tradeMode, setTradeMode] = useState('LONG'); // LONG, SHORT, SWAP
  const [pairData, setPairData] = useState({}); // Store volume and IO for each pair
  const { address, isConnected } = useAccount();
  
  // Smart Contract Integration
  const { 
    openFuturePosition: openPositionOnChain,
    checkApprovalNeeded,
    approveUSDT,
    isPending,
    isConfirming,
    isConfirmed,
    resetState 
  } = useIOTradingContract();

  const cryptos = [
    { symbol: 'BTC', name: 'Bitcoin', color: '#f7931a', icon: '₿', coinId: 'bitcoin' },
    { symbol: 'ETH', name: 'Ethereum', color: '#627eea', icon: 'Ξ', coinId: 'ethereum' },
    { symbol: 'SOL', name: 'Solana', color: '#14f195', icon: '◎', coinId: 'solana' },
    { symbol: 'DOGE', name: 'Dogecoin', color: '#c2a633', icon: 'Ð', coinId: 'dogecoin' },
    { symbol: 'BNB', name: 'Binance', color: '#f3ba2f', icon: 'B', coinId: 'binancecoin' },
    { symbol: 'XRP', name: 'XRP', color: '#23292f', icon: 'X', coinId: 'ripple' },
  ];

  const selectedCryptoData = cryptos.find(c => c.symbol === selectedCrypto);
  const currentPrice = livePrices[selectedCrypto] || 0;
  const priceChange = candleData.length > 1 
    ? currentPrice - candleData[0].open 
    : 0;
  const priceChangePercent = candleData.length > 1 
    ? ((priceChange / candleData[0].open) * 100).toFixed(2) 
    : '0.00';

  useEffect(() => {
    fetchLivePrices();
    fetchPriceHistory(); // Fetch immediately
    fetchPairData(); // Fetch volume and IO for all pairs
    const priceInterval = setInterval(fetchLivePrices, 10000);
    const pairDataInterval = setInterval(fetchPairData, 30000); // Fetch pair data every 30 seconds
    
    return () => {
      clearInterval(priceInterval);
      clearInterval(pairDataInterval);
    };
  }, []);

  useEffect(() => {
    fetchPriceHistory();
  }, [selectedCrypto, timeframe]);

  const fetchLivePrices = async () => {
    try {
      const response = await axios.get(
        'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,solana,dogecoin,binancecoin,ripple&vs_currencies=usd&include_24hr_change=true'
      );
      
      setLivePrices({
        BTC: response.data.bitcoin?.usd || 0,
        ETH: response.data.ethereum?.usd || 0,
        SOL: response.data.solana?.usd || 0,
        DOGE: response.data.dogecoin?.usd || 0,
        BNB: response.data.binancecoin?.usd || 0,
        XRP: response.data.ripple?.usd || 0,
      });
    } catch (error) {
      console.error('Error fetching prices:', error);
    }
  };

  const fetchPairData = async () => {
    try {
      const apiBaseUrl = 'https://api-iotrader.dev';
      const response = await axios.get(
        `${apiBaseUrl}/api/derivatives/contracts`
      );
      
      const contracts = response.data?.contracts || [];
      const pairDataMap = {};
      
      // Map contracts to pair data
      contracts.forEach((contract) => {
        // Extract symbol from ticker_id (e.g., "BTC-PERP" -> "BTC")
        const tickerId = contract.ticker_id || '';
        const symbol = tickerId.split('-')[0];
        
        if (symbol) {
          // Get base_volume and open_interest in base currency
          const baseVolume = parseFloat(contract.base_volume || 0);
          const openInterest = parseFloat(contract.open_interest || 0);
          
          pairDataMap[symbol] = { baseVolume, openInterest };
        }
      });
      
      setPairData(pairDataMap);
    } catch (error) {
      console.error('Error fetching pair data:', error);
      // Set empty data on error to prevent UI issues
      setPairData({});
    }
  };

  const fetchPriceHistory = async () => {
    try {
      setLoading(true);
      const crypto = cryptos.find(c => c.symbol === selectedCrypto);
      if (!crypto) return;

      const days = timeframe === '1H' ? 1 : timeframe === '6H' ? 1 : timeframe === '1D' ? 1 : timeframe === '1W' ? 7 : 30;
      
      const response = await axios.get(
        `https://api.coingecko.com/api/v3/coins/${crypto.coinId}/market_chart?vs_currency=usd&days=${days}`
      );
      
      const prices = response.data.prices;
      
      // Create candlestick data - more candles for better zoom
      const numCandles = 100; // Show 100 candles
      const candleSize = Math.max(1, Math.floor(prices.length / numCandles));
      const candles = [];
      
      for (let i = 0; i < prices.length; i += candleSize) {
        const slice = prices.slice(i, Math.min(i + candleSize, prices.length));
        if (slice.length === 0) continue;
        
        const open = slice[0][1];
        const close = slice[slice.length - 1][1];
        const high = Math.max(...slice.map(p => p[1]));
        const low = Math.min(...slice.map(p => p[1]));
        const timestamp = slice[0][0];
        
        const date = new Date(timestamp);
        const timeStr = date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true
        });
        
        candles.push({
          time: timeStr,
          open: open,
          high: high,
          low: low,
          close: close,
          timestamp: timestamp
        });
      }
      
      console.log('Candle data loaded:', candles.length, 'candles');
      setCandleData(candles);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching candle data:', error);
      setLoading(false);
    }
  };

  const openPosition = async (positionType) => {
    if (!isConnected) {
      alert('Please connect your wallet first!');
      return;
    }
    
    if (!address) {
      alert('Wallet address not available. Please reconnect your wallet.');
      return;
    }
    
    if (!betAmount || parseFloat(betAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      const actualAmount = parseFloat(betAmount);
      const isLong = positionType === 'LONG';
      const stakeUSDT = true; // Using USDT for staking
      
      console.log('Opening position:', { positionType, actualAmount, address, isConnected });
      
      // Step 1: Check if USDT approval is needed
      let approvalCheck;
      try {
        console.log('Checking if approval is needed...');
        approvalCheck = await checkApprovalNeeded(actualAmount);
        console.log('Approval check result:', approvalCheck);
      } catch (err) {
        console.error('Error in checkApprovalNeeded:', err);
        alert(`Error checking approval: ${err.message || 'Unknown error'}`);
        return;
      }
      
      // Check for errors in approval check result
      if (approvalCheck && approvalCheck.error) {
        alert(`Error checking approval: ${approvalCheck.error}`);
        return;
      }
      
      // Only proceed with approval if it's actually needed and we have the USDT address
      if (approvalCheck && approvalCheck.needsApproval) {
        if (!approvalCheck.usdtAddress) {
          alert('Error: USDT address not found');
          return;
        }
        
        alert('Please approve USDT spending in your wallet...');
        const approvalResult = await approveUSDT(approvalCheck.usdtAddress);
        
        if (!approvalResult || !approvalResult.success) {
          alert(`Approval failed: ${approvalResult?.error || 'Unknown error'}`);
          return;
        }
        
        alert('USDT approval confirmed! Now opening position...');
        
        // Reset state after approval to prepare for position opening
        resetState();
      }
      
      // Step 2: Open position on blockchain using USDT (skip approval since we handled it separately)
      const result = await openPositionOnChain(
        selectedCrypto,
        currentPrice,
        3600, // Duration: 1 hour default
        isLong,
        actualAmount, // Amount in USDT
        stakeUSDT, // stakeUSDT = true to use USDT
        true // skipApproval = true since we already handled approval
      );
      // If blockchain transaction fails, stop here
      if (!result.success) {
        alert(`Transaction failed: ${result.error}`);
        return;
      }

      // Step 2: Wait for blockchain transaction confirmation
      await new Promise((resolve) => {
        const checkConfirmation = setInterval(() => {
          if (isConfirmed) {
            clearInterval(checkConfirmation);
            resolve();
          }
        }, 1000);
        
        // Timeout after 2 minutes
        setTimeout(() => {
          clearInterval(checkConfirmation);
          resolve();
        }, 120000);
      });

      // Step 3: After blockchain confirmation, save to Firebase database
      const positionData = {
        userAddress: address,
        crypto: selectedCrypto,
        entryPrice: currentPrice,
        amount: actualAmount,
        leverage: leverage,
        positionType: positionType,
        timestamp: serverTimestamp(),
        status: 'open',
        onChain: true,
        confirmed: true
      };

      await addDoc(collection(db, 'futurePositions'), positionData);
      
      // Step 4: Show success message
      alert(`${positionType} position opened successfully!`);
      setBetAmount('10');
    } catch (error) {
      console.error('Error opening position:', error);
      alert('Failed to open position. Please try again.');
    }
  };

  return (
    <>
      <SEO
        title="Futures Trading - High Leverage Crypto Trading"
        description="Trade cryptocurrency futures with high leverage on BTC, ETH, BNB, SOL and more. Long or short positions with real-time charts and instant execution on BSC blockchain."
        keywords="crypto futures trading, leveraged trading, BTC futures, ETH futures, BNB futures, long short trading, cryptocurrency derivatives, DeFi trading"
        url="/future-trading"
      />
    <div className="w-full min-h-screen bg-[#111111]">
      <Header />
      
      {/* CONTENT WRAPPER */}
      <div>
      {/* Crypto Pair Selection Bar */}
      <div className="bg-[#141414] border-b border-[#f3b340] sticky top-16 z-40">
        <div className="max-w-full px-3 sm:px-6 py-2 sm:py-3">
          <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto scrollbar-hide">
            <button className="w-10 h-10 bg-[#263d55] hover:bg-[#2a4561] border border-[#f3b340] rounded-lg flex items-center justify-center text-[#f3b340] text-xl transition-colors">
              +
            </button>
            {cryptos.map((crypto) => (
              <button
                key={crypto.symbol}
                onClick={() => setSelectedCrypto(crypto.symbol)}
                className={`px-4 py-2 rounded-lg font-semibold text-sm globalFont whitespace-nowrap transition-colors flex items-center gap-2 border ${
                  selectedCrypto === crypto.symbol
                    ? 'gold-gradient text-black border-[#f3b340]'
                    : 'bg-[#263d55] text-white border-[#f3b340] hover:bg-[#2a4561]'
                }`}
              >
                <span className="text-xl">{crypto.icon}</span>
                <div className="text-left">
                  <div className="text-xs">{crypto.symbol}USDT</div>
                  <div className="text-xs font-normal">{livePrices[crypto.symbol]?.toFixed(2)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-full px-3 sm:px-6 py-3 sm:py-4 md:h-[calc(100vh-140px)] overflow-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 md:h-full">
          {/* Left Side - Chart */}
          <div className="lg:col-span-2 flex flex-col gap-3 sm:gap-4 md:h-full">
            {/* Asset Price Header */}
            <div className="bg-[#141414] border border-[#f3b340] rounded-lg p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <h2 className="text-white text-lg sm:text-2xl font-bold globalFont">{selectedCrypto}USDT</h2>
                  <span className="text-[#f3b340] text-base sm:text-xl">⚡</span>
                </div>
                <div className={`px-2 sm:px-3 py-1 rounded text-white text-xs sm:text-sm font-bold globalFont ${
                  priceChange >= 0 ? 'bg-green-500' : 'bg-red-500'
                }`}>
                  {currentPrice.toFixed(5)}
                </div>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2 sm:gap-4">
                <span className={`globalFont text-xs sm:text-sm ${priceChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {priceChange >= 0 ? '+' : ''}{priceChangePercent}% for a day
                </span>
                {(() => {
                  const pairInfo = pairData[selectedCrypto] || { baseVolume: 0, openInterest: 0 };
                  const formatNumber = (num) => {
                    if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
                    if (num >= 1000) return (num / 1000).toFixed(2) + 'K';
                    return num.toFixed(2);
                  };
                  return (
                    <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-400 globalFont">Base Volume:</span>
                        <span className="text-[#f3b340] font-semibold globalFont">{formatNumber(pairInfo.baseVolume)} USD</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-400 globalFont">Open Interest:</span>
                        <span className="text-[#f3b340] font-semibold globalFont">{formatNumber(pairInfo.openInterest)} USD</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Trading Chart */}
            <div className="bg-[#141414] border border-[#f3b340] rounded-lg p-0 flex-1 flex flex-col h-[800px] sm:h-[800px] md:h-900px]" style={{height: '800px'}}>
              <div className="flex items-center justify-between p-3 border-b border-[#f3b340]">
                <div className="text-white text-xs sm:text-sm globalFont">
                  {selectedCryptoData?.name}/USDT Chart
                </div>
                <div className={`px-2 py-1 rounded text-xs font-bold globalFont ${
                  priceChange >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {priceChange >= 0 ? '+' : ''}{priceChangePercent}%
                </div>
              </div>
              
              <div className="flex-1 rounded-b-lg overflow-hidden" >
                <iframe
                  src={`https://s.tradingview.com/widgetembed/?frameElementId=tradingview_chart&symbol=BINANCE:${selectedCrypto}USDT&interval=60&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=f1f3f6&studies=[]&theme=dark&style=1&timezone=Etc/UTC&studies_overrides={}&overrides={}&enabled_features=[]&disabled_features=[]&locale=en&utm_source=iotrading&utm_medium=widget&utm_campaign=chart&utm_term=BINANCE:${selectedCrypto}USDT`}
                  title="TradingView Chart"
                  className="w-full h-full"
                  frameBorder="0"
                  allowTransparency="true"
                  scrolling="no"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>

          {/* Right Side - Trading Form */}
          <div className="lg:col-span-1">
            {/* Trade Mode Tabs */}
            <div className="bg-[#141414] border border-[#f3b340] rounded-t-lg flex mb-0">
              <button
                onClick={() => setTradeMode('LONG')}
                className={`flex-1 py-3 text-sm font-bold globalFont transition-colors ${
                  tradeMode === 'LONG'
                    ? 'gold-gradient text-black'
                    : 'text-gray-400 hover:text-white hover:bg-[#263d55]'
                }`}
              >
                Long
              </button>
              <button
                onClick={() => setTradeMode('SHORT')}
                className={`flex-1 py-3 text-sm font-bold globalFont transition-colors border-l border-r border-[#f3b340]/30 ${
                  tradeMode === 'SHORT'
                    ? 'gold-gradient text-black'
                    : 'text-gray-400 hover:text-white hover:bg-[#263d55]'
                }`}
              >
                Short
              </button>
              <button
                onClick={() => setTradeMode('SWAP')}
                className={`flex-1 py-3 text-sm font-bold globalFont transition-colors ${
                  tradeMode === 'SWAP'
                    ? 'gold-gradient text-black'
                    : 'text-gray-400 hover:text-white hover:bg-[#263d55]'
                }`}
              >
                Swap
              </button>
            </div>

            {/* Futures Trading Form */}
            <div className="bg-[#141414] border border-[#f3b340] border-t-0 rounded-b-lg p-3 sm:p-4">
              <h3 className="text-white text-base sm:text-lg font-bold mb-3 sm:mb-4 globalFont">
                {tradeMode === 'SWAP' ? 'Token Swap' : `${tradeMode} Position`}
              </h3>
              
              {/* Current Price Display */}
              <div className="mb-3 sm:mb-4 bg-[#263d55] border border-[#f3b340] rounded-lg px-3 sm:px-4 py-2 sm:py-3">
                <div className="text-gray-400 text-xs mb-1 globalFont">Current Price</div>
                <div className="text-white text-xl sm:text-2xl font-bold globalFont">
                  ${currentPrice.toFixed(2)}
                </div>
                <div className={`text-xs sm:text-sm mt-1 globalFont ${priceChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {priceChange >= 0 ? '↑' : '↓'} {priceChangePercent}%
                </div>
              </div>

              {/* Amount */}
              <div className="mb-3 sm:mb-4">
                <label className="text-gray-400 text-xs mb-2 block globalFont">Amount (USDT)</label>
                <div className="bg-[#263d55] border border-[#f3b340] rounded-lg px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
                  <span className="text-gray-400 text-xs sm:text-sm globalFont">USDT</span>
                  <input
                    type="number"
                    value={betAmount}
                    onChange={(e) => setBetAmount(e.target.value)}
                    className="bg-transparent text-white text-right text-base sm:text-lg font-bold globalFont outline-none w-20 sm:w-24"
                    placeholder="10.00"
                  />
                </div>
              </div>

              {/* Leverage Slider */}
              {tradeMode !== 'SWAP' && (
                <div className="mb-3 sm:mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-gray-400 text-xs globalFont">Leverage</label>
                    <span className="text-[#f3b340] text-sm font-bold globalFont">{leverage}x</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={leverage}
                    onChange={(e) => setLeverage(parseInt(e.target.value))}
                    className="w-full h-2 bg-[#263d55] rounded-lg appearance-none cursor-pointer slider"
                    style={{
                      background: `linear-gradient(to right, #f3b340 0%, #f3b340 ${leverage}%, #263d55 ${leverage}%, #263d55 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1 globalFont">
                    <span>1x</span>
                    <span>25x</span>
                    <span>50x</span>
                    <span>100x</span>
                  </div>
                </div>
              )}

              {/* Position Size Info */}
              <div className="mb-3 sm:mb-4 bg-[#263d55] border border-[#f3b340] rounded-lg p-3">
                {tradeMode === 'SWAP' ? (
                  <>
                    <div className="flex justify-between text-xs text-gray-400 globalFont mb-2">
                      <span>You Pay:</span>
                      <span className="text-white">{betAmount || '0.00'} USDT</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 globalFont">
                      <span>You Receive:</span>
                      <span className="text-white">
                        {currentPrice > 0 ? (parseFloat(betAmount || 0) / currentPrice).toFixed(6) : '0.00'} {selectedCrypto}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-xs text-gray-400 globalFont mb-2">
                      <span>Position Size:</span>
                      <span className="text-white">${(parseFloat(betAmount || 0) * leverage).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 globalFont">
                      <span>Liquidation Price:</span>
                      <span className="text-red-400">${(currentPrice * 0.95).toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Action Buttons */}
              {tradeMode === 'SWAP' ? (
                <button
                  onClick={() => openPosition('SWAP')}
                  disabled={!isConnected}
                  className="w-full gold-gradient text-black py-4 rounded-lg font-bold text-base globalFont disabled:opacity-50 transition-all shadow-lg touch-manipulation hover:opacity-90"
                >
                  {!isConnected ? 'Connect Wallet to Swap' : 'Swap Now'}
                </button>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  <button
                    onClick={() => openPosition('SHORT')}
                    disabled={!isConnected || isPending || isConfirming}
                    className="bg-red-500 hover:bg-red-600 active:bg-red-700 text-white py-3 sm:py-3 rounded-lg font-bold globalFont disabled:opacity-50 transition-colors shadow-lg touch-manipulation"
                  >
                    <div className="text-xs sm:text-sm mb-1">Short</div>
                    <div className="text-xs">Sell</div>
                  </button>
                  <button
                    onClick={() => openPosition('LONG')}
                    disabled={!isConnected || isPending || isConfirming}
                    className="bg-green-500 hover:bg-green-600 active:bg-green-700 text-white py-3 sm:py-3 rounded-lg font-bold globalFont disabled:opacity-50 transition-colors shadow-lg touch-manipulation"
                  >
                    <div className="text-xs sm:text-sm mb-1">Long</div>
                    <div className="text-xs">Buy</div>
                  </button>
                </div>
              )}

              {/* Info Text */}
              <div className="mt-2 sm:mt-3 text-center">
                <p className="text-gray-400 text-xs globalFont">
                  {tradeMode === 'SWAP' 
                    ? 'Instant token swap at market price'
                    : 'Long = Profit from price increase | Short = Profit from price decrease'
                  }
                </p>
              </div>

              {/* Market Sentiment */}
              <div className="mt-3 sm:mt-4">
                <h4 className="text-gray-400 text-xs mb-2 globalFont">Market Sentiment</h4>
                <div className="bg-[#263d55] border border-[#f3b340] rounded-lg p-2 sm:p-3">
                  <div className="flex justify-between mb-2">
                    <span className="text-green-400 text-xs globalFont">Long: 62%</span>
                    <span className="text-red-400 text-xs globalFont">Short: 38%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{ width: '62%' }}></div>
                  </div>
                  <p className="text-gray-400 text-xs mt-2 globalFont">
                    Based on 2,456 open positions
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      {/* END CONTENT WRAPPER */}
    </div>
    </>
  );
};

export default FutureTrading;
